import React, { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import {
  getMonitoringRecentRuns,
  getMonitoringGenerationRuns,
  getMonitoringRefinementRuns,
  getMonitoringAdditionalRuns,
  getMonitoringCombinedRuns,
  getRecentModelComparisons
} from '../../services/api';
import { calculateLiveStats } from '../../utils/monitoringCalculations';
import { calculateComparisonStats, calculateComparisonBreakdown } from '../../utils/comparisonCalculations';
import LiveStats from './LiveStats';
import RecentRuns from './RecentRuns';
import ModelComparisonStats from './ModelComparisonStats';
import LatencyChart from './LatencyChart';
import SuccessRateChart from './SuccessRateChart';
import TokenUsageChart from './TokenUsageChart';
import CostChart from './CostChart';
import PerformanceChart from './PerformanceChart';
import ActivityChart from './ActivityChart';

const MonitoringDashboard = () => {
  const { currentWorkspace } = useWorkspace();
  
  const [activeTab, setActiveTab] = useState('combined');
  const [liveStats, setLiveStats] = useState(null);
  const [recentRuns, setRecentRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Model comparison stats
  const [comparisonStats, setComparisonStats] = useState(null);
  const [comparisonBreakdown, setComparisonBreakdown] = useState(null);
  const [recentComparisons, setRecentComparisons] = useState(null);

  const fetchMonitoringData = useCallback(async () => {
    if (!currentWorkspace) return;

    try {
      setError(null);
      
      // Handle model comparison tab separately
      if (activeTab === 'comparisons') {
        // Only fetch comparisons data, compute stats on frontend
        const comparisonsResult = await getRecentModelComparisons(currentWorkspace.id, 50);

        if (comparisonsResult.success) {
          const comparisonsData = comparisonsResult.data;
          setRecentComparisons(comparisonsData);
          
          // Compute stats and breakdown directly from comparisons data
          const comparisons = comparisonsData.comparisons || [];
          setComparisonStats(calculateComparisonStats(comparisons));
          setComparisonBreakdown(calculateComparisonBreakdown(comparisons));
        }

        setLoading(false);
        return;
      }
      
      let runsApiCall;
      switch (activeTab) {
        case 'generation':
          runsApiCall = getMonitoringGenerationRuns(currentWorkspace.id, 500);
          break;
        case 'refinements':
          runsApiCall = getMonitoringRefinementRuns(currentWorkspace.id, 500);
          break;
        case 'additional':
          runsApiCall = getMonitoringAdditionalRuns(currentWorkspace.id, 500);
          break;
        case 'combined':
          runsApiCall = getMonitoringCombinedRuns(currentWorkspace.id, 500);
          break;
        default:
          runsApiCall = getMonitoringRecentRuns(currentWorkspace.id, 500);
      }
      
      // Only fetch runs data, compute stats on frontend
      const runsResult = await runsApiCall;

      if (runsResult.success) {
        const runs = runsResult.data.runs || [];
        setRecentRuns(runs);
        
        // Compute stats directly from runs data
        setLiveStats(calculateLiveStats(runs));
      }

    } catch (err) {
      setError('Failed to load monitoring data');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace, activeTab]);

  useEffect(() => {
    fetchMonitoringData();
  }, [fetchMonitoringData]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchMonitoringData();
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchMonitoringData]);

  const handleRefresh = () => {
    setLoading(true);
    fetchMonitoringData();
  };

  if (!currentWorkspace) {
    return (
      <div className="flex-1 flex items-center justify-center bg-bg-primary p-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-bg-tertiary border-2 border-border-primary flex items-center justify-center mx-auto mb-5">
            <svg className="w-10 h-10 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-text-primary mb-2">No Workspace Selected</h3>
          <p className="text-sm text-text-secondary leading-relaxed">Please select a workspace to view monitoring data</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { 
      id: 'combined', 
      label: 'All',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      )
    },
    { 
      id: 'runs', 
      label: 'Runs',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    { 
      id: 'generation', 
      label: 'Generation',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    { 
      id: 'refinements', 
      label: 'Refinements',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      )
    },
    { 
      id: 'additional', 
      label: 'Additional',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      )
    },
    { 
      id: 'comparisons', 
      label: 'Model Comparisons',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <rect x="3" y="3" width="7" height="18" rx="1" />
          <rect x="14" y="3" width="7" height="18" rx="1" />
          <path d="M12 8V16" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="2 2" />
        </svg>
      )
    }
  ];

  return (
    <div className="monitoring-page flex-1 flex flex-col bg-bg-primary overflow-hidden">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <div>
            <h1 className="page-title">Analytics</h1>
            <p className="page-subtitle">
              Real-time performance and usage insights
            </p>
          </div>
        </div>
        
        <div className="page-actions">
          <label className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-bg-tertiary border border-border-primary hover:bg-bg-hover transition-colors cursor-pointer select-none">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-border-primary focus:ring-offset-0 bg-bg-input cursor-pointer accent-[#32CD32]"
            />
            <span className="text-text-secondary text-xs sm:text-sm whitespace-nowrap">Auto-refresh</span>
          </label>
          
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="btn-secondary"
            title="Refresh data"
          >
            <svg
              className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border-primary bg-bg-primary px-4 sm:px-6">
        <nav className="-mb-px flex gap-2 overflow-x-auto scrollbar-hide" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                group inline-flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm whitespace-nowrap transition-all duration-200
                ${activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-text-tertiary hover:text-text-primary hover:border-border-primary'
                }
              `}
            >
              <span className={`transition-transform duration-200 ${activeTab === tab.id ? 'scale-110' : 'group-hover:scale-105'}`}>
                {tab.icon}
              </span>
              <span>{tab.label}</span>
              {activeTab === tab.id && (
                <span className="inline-flex items-center justify-center w-1.5 h-1.5 rounded-full bg-primary-500"></span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex-shrink-0 mx-4 sm:mx-6 mt-4 bg-error/10 border border-error/30 rounded-lg p-3 sm:p-4 flex items-center gap-3">
          <svg className="w-5 h-5 text-error flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      {/* Content */}
      <div className="monitoring-content flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6 bg-bg-primary">
        <div className="max-w-7xl mx-auto space-y-4">
          {/* Conditional Content Based on Active Tab */}
          {activeTab === 'comparisons' ? (
            <ModelComparisonStats
              stats={comparisonStats}
              breakdown={comparisonBreakdown}
              recentComparisons={recentComparisons}
              loading={loading}
            />
          ) : (
            <>
              {/* Stats */}
              <LiveStats
                stats={liveStats}
                loading={loading}
              />

              {/* Charts Section - Row 1 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <LatencyChart
                  runs={recentRuns}
                  loading={loading}
                />
                <SuccessRateChart
                  runs={recentRuns}
                  loading={loading}
                />
              </div>

              {/* Charts Section - Row 2 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <TokenUsageChart
                  runs={recentRuns}
                  loading={loading}
                />
                <PerformanceChart
                  runs={recentRuns}
                  loading={loading}
                />
              </div>

              {/* Charts Section - Row 3 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <CostChart
                  runs={recentRuns}
                  loading={loading}
                />
                <ActivityChart
                  runs={recentRuns}
                  loading={loading}
                />
              </div>

              {/* Main content - Full width table */}
              <div>
                <RecentRuns
                  runs={recentRuns}
                  loading={loading}
                  onRefresh={handleRefresh}
                  runType={activeTab}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonitoringDashboard;
